import { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { TableContainer, TablePagination, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress, Box } from "@mui/material";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Title from "../../../../common/components/Title";
import { BASE_URL } from "../../../../api/config";
import { Reservation } from "../../../../../types/reservation";
import { useUserContext } from "../../../../context/UserContext";
import dayjs from "dayjs";
import YellowButton from "common/components/YellowButton";
import ReplayIcon from "@mui/icons-material/Replay";
import WarningIcon from "@mui/icons-material/Warning";

function preventDefault(event: React.MouseEvent) {
  event.preventDefault();
}

export default function Reservations() {
  const { user } = useUserContext();
  const [status] = useState("SUCCESS");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [reservations, setReservations] = useState<Array<Reservation>>([]);
  const [missingEmails, setMissingEmails] = useState<Array<Reservation>>([]);
  const [showMissingEmails, setShowMissingEmails] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ message: string; sent?: number; failed?: number } | null>(null);

  useEffect(() => {
    if (user) {
      const query = `?status=${status}`;
      fetch(BASE_URL + "reservations" + query, {
        headers: {
          "x-auth-token": user.token,
        },
      })
        .then((data) => {
          if (data.status === 200) {
            return data.json();
          }
          throw new Error("Algo malo ocurrió");
        })
        .then((response) => setReservations(response as Array<Reservation>));
    }
  }, [status, user, page, rowsPerPage]);

  const fetchMissingEmails = () => {
    if (!user) return;
    fetch(BASE_URL + "reservations/missing-emails?startDate=2026-04-30", {
      headers: { "x-auth-token": user.token },
    })
      .then((res) => res.json())
      .then((data) => {
        setMissingEmails(data.reservations || []);
        setShowMissingEmails(true);
      })
      .catch((err) => console.error("Error fetching missing emails:", err));
  };

  const handleResendSingle = async (id: number, email: string) => {
    if (!user) return;
    if (!window.confirm(`Reenviar correo a ${email}?`)) return;
    setSending(true);
    try {
      const response = await fetch(BASE_URL + `reservations/resend-email/${id}`, {
        method: "POST",
        headers: { "x-auth-token": user.token },
      });
      const data = await response.json();
      setSendResult(data);
      if (response.ok) {
        setMissingEmails((prev) => prev.filter((r) => r.id !== id));
      }
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  const handleResendAll = async () => {
    if (!user || missingEmails.length === 0) return;
    if (!window.confirm(`Reenviar correo a ${missingEmails.length} reservas pendientes?`)) return;
    setSending(true);
    setSendResult(null);
    try {
      const response = await fetch(BASE_URL + "reservations/resend-all-missing-emails", {
        method: "POST",
        headers: { "x-auth-token": user.token, "Content-Type": "application/json" },
        body: JSON.stringify({ startDate: "2026-04-30" }),
      });
      const data = await response.json();
      setSendResult(data);
      if (response.ok && data.sent === missingEmails.length) {
        setMissingEmails([]);
        setShowMissingEmails(false);
      }
    } catch (err) {
      console.error(err);
    }
    setSending(false);
  };

  const handleSubmit = () => {
    if (user) {
      fetch(BASE_URL + "report/getReport", {
        method: "POST",
        headers: {
          "x-auth-token": user.token,
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.blob();
          }
          throw new Error("Algo malo ocurrió");
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "report.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          console.error("Error downloading the file:", error);
        });
    }
  };

  const columns = [
    { field: "session_id", headerName: "Order Id", minWidth: 150 },
    { field: "name", headerName: "Nombre", minWidth: 150 },
    { field: "email", headerName: "Email", minWidth: 150 },
    { field: "phone", headerName: "Teléfono", minWidth: 150 },
    { field: "program.name", headerName: "Programa", minWidth: 150 },
    { field: "room.name", headerName: "Habitación", minWidth: 150 },
    { field: "start_date", headerName: "Checkin", minWidth: 150 },
    { field: "end_date", headerName: "Checkout", minWidth: 150 },
    {
      field: "purchase_date",
      headerName: "Fecha de transacción",
      minWidth: 150,
    },
    { field: "adults", headerName: "N de adultos", minWidth: 150 },
    { field: "number_of_rooms", headerName: "N de habitación", minWidth: 150 },
    { field: "total_price", headerName: "Precio total", minWidth: 150 },
    {
      field: "transaction_id",
      headerName: "Codigo de Autorización",
      minWidth: 150,
    },
  ];

  return (
    <>
      <Title>Reservas</Title>
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <YellowButton
          className="button-descarga"
          handleSubmit={handleSubmit}
          text="Descargar Reporte"
        />
        <YellowButton
          className="button-missing-emails"
          handleSubmit={fetchMissingEmails}
          text={`Correos Pendientes${missingEmails.length > 0 ? ` (${missingEmails.length})` : ""}`}
        />
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell className="column">{column.headerName}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reservations
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow key={row.id}>
                  {columns.map((column) => {
                    let fieldValue = null;
                    if (column.field.includes(".")) {
                      const [parentField, childField] = column.field.split(".");
                      if (
                        (row[parentField as keyof Reservation] as any)?.[
                          childField
                        ]
                      ) {
                        fieldValue = (
                          row[parentField as keyof Reservation] as any
                        )[childField as keyof typeof row.id];
                      }
                    } else {
                      fieldValue = row[column.field as keyof Reservation];
                    }

                    if (
                      column.field === "start_date" ||
                      column.field === "end_date" ||
                      column.field === "purchase_date"
                    ) {
                      fieldValue = dayjs(fieldValue).format("YYYY-MM-DD HH:mm");
                    }

                    return (
                      <TableCell key={column.field}>{fieldValue}</TableCell>
                    );
                  })}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25, 100]}
        count={reservations.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) =>
          setRowsPerPage(parseInt(event.target.value, 10))
        }
      />

      <Dialog open={showMissingEmails} onClose={() => setShowMissingEmails(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon color="warning" />
              Reservas sin correo enviado ({missingEmails.length})
            </Box>
            {missingEmails.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleResendAll}
                disabled={sending}
                startIcon={sending ? <CircularProgress size={20} /> : <ReplayIcon />}
              >
                Reenviar Todos
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {sendResult && (
            <Box mb={2} p={2} bgcolor={sendResult.failed ? "warning.light" : "success.light"} borderRadius={1}>
              <Typography>{sendResult.message}</Typography>
            </Box>
          )}
          {missingEmails.length === 0 ? (
            <Typography align="center" sx={{ py: 4 }}>
              No hay correos pendientes por enviar
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Check-out</TableCell>
                    <TableCell>Precio</TableCell>
                    <TableCell>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {missingEmails.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.start_date}</TableCell>
                      <TableCell>{r.end_date}</TableCell>
                      <TableCell>CLP {new Intl.NumberFormat("es-CL").format(r.total_price)}</TableCell>
                      <TableCell>
                        <Tooltip title="Reenviar correo">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleResendSingle(r.id, r.email)}
                              disabled={sending}
                            >
                              {sending ? <CircularProgress size={20} /> : <ReplayIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowMissingEmails(false); setSendResult(null); }}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
